import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import TerminalIcon from '@mui/icons-material/Terminal';
import { api } from '../services/api';

const DEFAULT_RTTY_PORT = 443;
const MIN_PORT = 1;
const MAX_PORT = 65535;
const PORT_PRESETS = [
  { port: 443, hint: 'Standard HTTPS' },
  { port: 5912, hint: 'Legacy RTTY' },
];

const parsePort = (value) => {
  const n = Number(value);
  if (!Number.isInteger(n) || n < MIN_PORT || n > MAX_PORT) return null;
  return n;
};

const ProjectConfiguration = () => {
  const [portInput, setPortInput] = useState(String(DEFAULT_RTTY_PORT));
  const [savedPort, setSavedPort] = useState(DEFAULT_RTTY_PORT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const parsedPort = parsePort(portInput);
  const portValid = parsedPort !== null;
  const dirty = portValid && parsedPort !== savedPort;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await api.getNMSConfiguration();
        const next = parsePort(data?.rtty_connect_port) ?? DEFAULT_RTTY_PORT;
        if (!cancelled) {
          setPortInput(String(next));
          setSavedPort(next);
        }
      } catch (error) {
        if (!cancelled) setStatus({ type: 'error', message: error.message || 'Failed to load settings' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    if (parsedPort === null) {
      setStatus({ type: 'error', message: `Enter a TCP port between ${MIN_PORT} and ${MAX_PORT}.` });
      return;
    }
    setSaving(true);
    setStatus({ type: '', message: '' });
    try {
      const data = await api.setNMSConfiguration({ rtty_connect_port: parsedPort });
      const next = parsePort(data?.rtty_connect_port) ?? parsedPort;
      setPortInput(String(next));
      setSavedPort(next);
      setStatus({ type: 'success', message: `Saved. New remote-terminal sessions will connect on port ${next}.` });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 760 }}>
      {/* Page header */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <SettingsEthernetIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Settings</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Project-wide settings. These apply to every device in this project.
      </Typography>

      {status.message && (
        <Alert severity={status.type || 'info'} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
          {status.message}
        </Alert>
      )}

      {/* Remote Terminal card */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <TerminalIcon sx={{ mt: 0.25, color: 'text.secondary' }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Remote Terminal (RTTY)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The outbound port each device dials to reach the RTTY server. Default is 443 (usually open on
              secured networks). Switch to 5912 only if the site blocks 443.
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Quick select
              </Typography>
              <Stack direction="row" spacing={1}>
                {PORT_PRESETS.map(({ port, hint }) => (
                  <Chip
                    key={port}
                    label={`${port} · ${hint}`}
                    onClick={() => setPortInput(String(port))}
                    color={parsedPort === port ? 'primary' : 'default'}
                    variant={parsedPort === port ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Stack>
            </Box>

            <TextField
              label="Connect port"
              type="number"
              value={portInput}
              onChange={(e) => setPortInput(e.target.value)}
              error={!portValid}
              helperText={portValid
                ? (parsedPort === savedPort ? `Active: devices use ${savedPort}` : 'Any TCP port from 1 to 65535')
                : `Enter a port from ${MIN_PORT} to ${MAX_PORT}`}
              inputProps={{ min: MIN_PORT, max: MAX_PORT, step: 1 }}
              size="small"
              sx={{ maxWidth: 240 }}
            />

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !dirty}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              {dirty && !saving && (
                <Button
                  variant="text"
                  color="inherit"
                  onClick={() => setPortInput(String(savedPort))}
                >
                  Reset
                </Button>
              )}
            </Stack>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default ProjectConfiguration;
